import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Search, AlertCircle, CheckCircle2 } from "lucide-react";
import { ItemCard } from './ItemCard';
import { CategoryFilter } from './CategoryFilter';

interface TemplateItem {
  id: string;
  name: string;
  type: string;
  description?: string;
  category?: string;
  min_value?: number;
  max_value?: number;
  unit?: string;
  required?: boolean;
}

interface Template {
  id: string;
  name: string;
  description?: string;
  frequency: string;
  items: TemplateItem[];
}

interface ItemCustomizationStepProps {
  template: Template;
  onNext: (selectedItems: TemplateItem[]) => void;
  onBack: () => void;
}

export const ItemCustomizationStep = ({ template, onNext, onBack }: ItemCustomizationStepProps) => {
  const [selectedItems, setSelectedItems] = useState<TemplateItem[]>(
    template.items.filter(item => item.required !== false).slice(0, getMinimumItems(template.frequency))
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  function getMinimumItems(frequency: string): number {
    switch (frequency?.toLowerCase()) {
      case 'daily': return 4;
      case 'weekly': return 6;
      case 'monthly': return 8;
      default: return 4;
    }
  }

  const minimumRequired = getMinimumItems(template.frequency);
  const isValidSelection = selectedItems.length >= minimumRequired;

  // Get unique categories from template items
  const categories = useMemo(() => {
    const cats = new Set<string>();
    template.items.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats);
  }, [template.items]);

  // Filter items based on search and category
  const filteredItems = useMemo(() => {
    return template.items.filter(item => {
      const matchesSearch = !searchQuery || 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [template.items, searchQuery, selectedCategory]);

  const toggleItem = (item: TemplateItem) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(selected => selected.id === item.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const selectAll = () => {
    setSelectedItems([...template.items]);
  };

  const selectMinimum = () => {
    const requiredItems = template.items.filter(item => item.required !== false);
    const minimumSelection = requiredItems.slice(0, minimumRequired);
    setSelectedItems(minimumSelection);
  };

  const handleNext = () => {
    if (isValidSelection) {
      onNext(selectedItems);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Customize Checklist Items
            {isValidSelection ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-yellow-600" />
            )}
          </CardTitle>
          <CardDescription>
            Select the items that apply to your location. You need at least {minimumRequired} items for {template.frequency} checklists.
            Currently selected: {selectedItems.length} / {template.items.length} items
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress indicator */}
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${
                  isValidSelection ? 'bg-green-600' : 'bg-yellow-500'
                }`}
                style={{ width: `${Math.min(100, (selectedItems.length / minimumRequired) * 100)}%` }}
              />
            </div>
            <Badge variant={isValidSelection ? "default" : "secondary"}>
              {selectedItems.length} / {minimumRequired} min
            </Badge>
          </div>

          {!isValidSelection && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <span className="text-sm text-yellow-800">
                Select at least {minimumRequired - selectedItems.length} more item(s) to continue
              </span>
            </div>
          )}

          {/* Search and filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />
          </div>

          {/* Quick actions */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectMinimum}>
              Select Minimum ({minimumRequired})
            </Button>
            <Button variant="outline" size="sm" onClick={selectAll}>
              Select All ({template.items.length})
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setSelectedItems([])}
              disabled={selectedItems.length === 0}
            >
              Clear All
            </Button>
          </div>

          <Separator />

          {/* Items grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredItems.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                isSelected={selectedItems.some(selected => selected.id === item.id)}
                onToggle={() => toggleItem(item)}
              />
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No items found matching your search criteria.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button 
          onClick={handleNext}
          disabled={!isValidSelection}
        >
          Next: Select Location
        </Button>
      </div>
    </div>
  );
};